<template>
  <LoadingContainer :is-loading="isLoadingBanners && !isLoadedBanners">
    <div class="card">
      <PaginatedListHeaderTemplate
        :total="banners.length"
        :selections="selections"
        :has-selections="hasSelections"
        title="Banners (Τραβήξτε για αναδιάταξη)"
      >
        <template v-slot:options>
          <router-link
            :to="{ name: 'marketing.giftActions.banners.add' }"
            class="button"
          >
            <span class="icon is-small"><i class="fa fa-plus"/></span
            ><span>Προσθήκη</span>
          </router-link>
        </template>
      </PaginatedListHeaderTemplate>
      <div class="card-content">
        <Filters
          :options="options"
          :is-loading="isFiltering"
          :filters="paginationParams.filters"
          general-label="Αναζήτηση με χρήση τίτλου"
          @navigate="navigate"
        />

        <NewPagination
          :items="banners"
          :total="paginationParams.count"
          :current-page="paginationParams.page"
          :pages-count="paginationParams.pages"
          :items-per-page="paginationParams.size"
          :is-loading="isLoading"
          @navigate="navigate"
        />

        <div class="columns">
          <div class="column is-full">
            <LoadingContainer
              :is-loading="isFiltering && isLoadedBanners"
              :is-opaque="true"
              class="table-wrapper"
            >
              <table
                v-if="banners.length"
                class="table is-bordered is-striped is-marginless is-fullwidth"
              >
                <thead>
                  <tr>
                    <th>#</th>
                    <th>
                      <sortable-header
                        :sort-options="paginationParams.sort"
                        :hide-sort-buttons="true"
                        label="Banner"
                        attribute="photo"
                        @sort="sort"
                      />
                    </th>
                    <th>
                      <sortable-header
                        :sort-options="paginationParams.sort"
                        label="Τίτλος"
                        attribute="title"
                        @sort="sort"
                      />
                    </th>
                    <th>
                      <sortable-header
                        :sort-options="paginationParams.sort"
                        label="Εμφανές"
                        attribute="active"
                        @sort="sort"
                      />
                    </th>
                    <th class="hidden-print">Ενέργειες</th>
                  </tr>
                </thead>
                <draggable
                  :value="banners"
                  :element="'tbody'"
                  @change="onReorder"
                >
                  <Banner
                    v-for="(item, index) in banners"
                    :item="item"
                    :index="paginationParams.indexStartsFrom + index"
                    :key="item.id"
                    :selections="selections"
                    @changeItemActive="changeItemActive"
                    @askToDeleteItem="askToDeleteItem"
                  />
                </draggable>
              </table>
              <h4 v-else class="title is-4 has-text-centered">
                Δε βρέθηκαν banners
              </h4>
            </LoadingContainer>
          </div>
        </div>

        <NewPagination
          v-if="banners.length"
          :items="banners"
          :total="paginationParams.count"
          :current-page="paginationParams.page"
          :pages-count="paginationParams.pages"
          :items-per-page="paginationParams.size"
          :is-loading="isLoading"
          @navigate="navigate"
        />
      </div>

      <confirmation
        :model-name="selectedItem.title"
        :is-open="isOpen"
        description="Είστε βέβαιος για τη διαγραφή αυτού του banner;"
        @closeModal="closeModal"
      />
    </div>
  </LoadingContainer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import draggable from 'vuedraggable';
import request from '@/utils/request';
import PaginatedList from '@/views/components/PaginatedList';
import PaginatedListHeaderTemplate from '@/views/components/PaginatedList/components/PaginatedListHeaderTemplate';
import Banner from './components/Banner';

export default {
  components: {
    draggable,
    Banner,
    PaginatedListHeaderTemplate,
  },
  extends: PaginatedList,
  metaInfo: {
    title: 'Banners',
  },
  data: () => ({
    options: [
      {
        label: 'Γενική Αναζήτηση',
        title: 'general',
        type: 'string',
        value: null,
      },
      {
        label: 'Προβολή',
        title: 'active',
        type: 'boolean',
        value: null,
      },
    ],
  }),
  computed: {
    ...mapGetters({
      banners: 'giftActionsBanners/list/getData',
      paginationParams: 'giftActionsBanners/list/getPaginationParams',
      isLoadingBanners: 'giftActionsBanners/list/getIsLoading',
      isLoadedBanners: 'giftActionsBanners/list/getIsLoaded',
    }),
  },
  watch: {
    $route: {
      async handler(newVal) {
        try {
          if (newVal.query.page) {
            // Redirections & filtering
            this.isFiltering = true;
            await this.getBanners(newVal.query);
            this.isFiltering = false;
          } else {
            // Initial loading without query params
            this.$router.replace({
              name: this.$route.name,
              query: this.filterParams({
                page: 1,
                size: 50,
                filters: {},
                sort: {},
                paginated: 1,
              }),
            });
          }
        } catch (err) {
          this.isFiltering = false;
          this.$notify({
            type: 'error',
            title: 'Αποτυχία',
            text: 'To αίτημα απέτυχε',
          });
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions({
      getBanners: 'giftActionsBanners/list/getBanners',
    }),
    async navigate(params = {}) {
      try {
        this.$router.push({
          name: this.$route.name,
          query: this.filterParams({ ...this.paginationParams, ...params }),
        });
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      }
    },
    async changeItemActive(selectedItem) {
      try {
        const isActive = !selectedItem.active;

        await request.put(`/gift-actions-banners/${selectedItem.id}/active`, {
          active: !!isActive,
        });

        await this.navigate({
          timestamp: new Date().getTime(),
        });

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: isActive
            ? 'Το banner δημοσιεύτηκε επιτυχώς!!'
            : 'Το banner δεν προβάλλεται πλέον',
        });
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      }
    },
    async closeModal(response) {
      console.log(this.selectedItem);
      try {
        this.isOpen = !this.isOpen;

        if (response) {
          const { data } = await request.delete(
            `/gift-actions-banners/${this.selectedItem.id}`,
          );

          if (data.error) {
            throw Error('Server-side validation failed');
          }

          await this.navigate({
            timestamp: new Date().getTime(),
          });

          this.$notify({
            type: 'success',
            title: 'Επιτυχία',
            text: 'Το banner διαγράφτηκε επιτυχώς!!',
          });
        }
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      }
    },
    async onReorder({ moved: { newIndex, oldIndex } }) {
      try {
        const { data } = await request.put('/gift-actions-banners-reorder', {
          newIndex,
          oldIndex,
        });

        if (data.error) {
          throw Error('Server-side validation failed');
        }

        await this.navigate();

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Τα banners αναδιατάχτηκαν επιτυχώς!!',
        });
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      }
    },
  },
};
</script>
